import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Target, Layers, Sparkles, Megaphone, FileBarChart, Archive, Search, ArrowLeft,
  Loader2, Plus, Image as ImageIcon, Video, FileText, CheckCircle2, Circle, LayoutGrid,
} from "lucide-react";
import api, { formatError } from "@/lib/api";
import { toast } from "sonner";
import { DeleteX } from "@/components/DeleteX";
import { useProjects } from "@/context/ProjectContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import ReviewWidget from "@/components/ReviewWidget";
import { BrandDocButton } from "@/components/BrandDocButton";
import { Counters, StartHere } from "@/components/workspace/WorkspacePanels";
import { Reminders } from "@/pages/Dashboard";

const SECTIONS = [
  { id: "full", label: "مشاريع الاستراتيجية", hint: "استراتيجية كاملة حتى القياس", icon: Target, tone: "from-primary/30 to-primary/5 text-primary" },
  { id: "ready", label: "مشاريع المحتوى", hint: "محتوى وحملات بخطة جاهزة", icon: Layers, tone: "from-accent/30 to-accent/5 text-accent" },
  { id: "standalone", label: "المحتوى المتنوّع", hint: "صور وفيديو ونصوص فردية", icon: Sparkles, tone: "from-pink-500/30 to-pink-500/5 text-pink-400" },
  { id: "campaigns", label: "الحملات", hint: "منشوراتك المجدولة", icon: Megaphone, tone: "from-sky-500/30 to-sky-500/5 text-sky-400" },
  { id: "reports", label: "التقارير", hint: "نتائج وأداء مشاريعك", icon: FileBarChart, tone: "from-emerald-500/30 to-emerald-500/5 text-emerald-400" },
  { id: "archived", label: "المؤرشفة", hint: "مشاريع مغلقة محفوظة", icon: Archive, tone: "from-slate-500/30 to-slate-500/5 text-slate-300" },
];


const PROJECT_SECTIONS = SECTIONS.filter((s) => !["reports"].includes(s.id));
const REPORT_TYPES = [
  { id: "progress", label: "تقارير تقدّم المشاريع", hint: "أين وصل كل مشروع ونسبة الإنجاز", icon: FileBarChart },
  { id: "strategy", label: "تقارير استراتيجية المشاريع", hint: "الاستراتيجية والأهداف والقياس", icon: Target },
  { id: "brand", label: "وثيقة هوية العلامة", hint: "ملف Word «هوية-اسم المشروع» قابل للتحرير", icon: Sparkles },
];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("ar-SA-u-ca-gregory-nu-latn", { day: "numeric", month: "short" }) : "");

function SectionTile({ s, count, active, onClick, i }) {
  const Icon = s.icon;
  return (
    <motion.button initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
      onClick={onClick} data-testid={`ws-section-${s.id}`}
      className={`relative text-start rounded-2xl border p-4 transition-colors duration-200 ${active ? "border-primary bg-primary/10 shadow-lg shadow-primary/10" : "border-border bg-card hover:border-primary/50"}`}>
      <div className={`w-11 h-11 rounded-xl grid place-items-center bg-gradient-to-br ${s.tone} mb-3`}><Icon className="w-5 h-5" /></div>
      <div className="font-bold text-sm">{s.label}</div>
      <div className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">{s.hint}</div>
      <span className="absolute top-3 end-3 min-w-7 h-7 px-2 rounded-full bg-secondary text-xs font-bold grid place-items-center" data-testid={`ws-count-${s.id}`}>{count}</span>
      {active && <motion.span layoutId="ws-active" className="absolute -bottom-px right-6 left-6 h-0.5 bg-primary rounded-full" />}
    </motion.button>
  );
}

const SHORT = { strategy: "الاستراتيجية", goals: "الأهداف", plan: "الحملة", execution: "التنفيذ", measure: "القياس", content: "المحتوى" };

function StageChips({ p }) {
  const stages = p.stages || [];
  return (
    <div className="min-w-0" data-testid={`ws-stages-${p.id}`}>
      <div className="grid gap-1 min-w-0" style={{ gridTemplateColumns: `repeat(${stages.length || 1}, minmax(0, 1fr))` }}>
        {stages.map((st, i) => (
          <div key={st.key} className="flex-1 min-w-0" data-testid={`ws-stage-${p.id}-${st.key}`}>
            <div className={`h-1.5 rounded-full ${st.done ? "bg-emerald-500" : st.current ? "bg-secondary overflow-hidden" : "bg-secondary"}`}>
              {!st.done && st.current && <div className="h-full bg-accent rounded-full" style={{ width: `${Math.max(8, st.pct || 0)}%` }} />}
            </div>
            <div className={`mt-1.5 flex items-center gap-1 text-[10px] sm:text-[11px] truncate ${st.done ? "text-emerald-400" : st.current ? "text-accent font-bold" : "text-muted-foreground"}`}>
              {st.done ? <CheckCircle2 className="w-3 h-3 shrink-0" /> : st.current ? <span className="w-2 h-2 rounded-full bg-accent animate-pulse shrink-0" /> : <Circle className="w-3 h-3 shrink-0" />}
              <span className="truncate" title={st.label}>{SHORT[st.key] || st.label}</span>
            </div>
          </div>
        ))}
      </div>
      {p.posts_total > 0 && (
        <p className="text-[11px] text-muted-foreground mt-2" data-testid={`ws-exec-${p.id}`}>
          التنفيذ: أُنتج {p.posts_produced}/{p.posts_total} · رُفع {p.posts_uploaded}/{p.posts_total} · قيس {p.posts_measured}/{p.posts_total}
        </p>
      )}
    </div>
  );
}

function ProjectCard({ p, onOpen, onReport, onPurge }) {
  const c = p.content || {};
  return (
    <div className="rounded-2xl border border-border bg-card p-4 sm:p-5 hover:border-primary/40 transition-colors min-w-0 overflow-hidden" data-testid={`ws-project-${p.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h3 className="font-bold truncate">{p.name}</h3>
          <p className="text-xs text-muted-foreground">{p.business_type} · {fmtDate(p.created_at)}</p>
        </div>
        <div className="text-end shrink-0">
          <span className="text-lg font-black text-primary" data-testid={`ws-progress-${p.id}`}>{p.progress}%</span>
          <p className="text-[10px] text-muted-foreground">الإنجاز العام</p>
        </div>
      </div>
      <StageChips p={p} />
      <div className="flex flex-wrap gap-3 text-[11px] text-muted-foreground mt-3">
        <span className="inline-flex items-center gap-1"><ImageIcon className="w-3 h-3" /> {(c.image || 0) + (c.logo || 0)}</span>
        <span className="inline-flex items-center gap-1"><Video className="w-3 h-3" /> {c.video || 0}</span>
        <span className="inline-flex items-center gap-1"><FileText className="w-3 h-3" /> {(c.caption || 0) + (c.script || 0)}</span>
        <span className="inline-flex items-center gap-1"><Megaphone className="w-3 h-3" /> {p.campaigns}</span>
      </div>
      <div className="flex gap-2 mt-4">
        <Button size="sm" onClick={() => onOpen(p)} className="flex-1 min-w-0 bg-primary hover:bg-primary/90 text-white gap-1" data-testid={`ws-continue-${p.id}`}>
          <span className="truncate">{p.status === "archived" ? "استعراض" : `متابعة: ${p.next_label}`}</span> <ArrowLeft className="w-3.5 h-3.5 shrink-0" />
        </Button>
        {p.has_report && (
          <Button size="sm" variant="outline" onClick={() => onReport(p)} className="border-border gap-1" data-testid={`ws-report-${p.id}`}>
            <FileBarChart className="w-3.5 h-3.5" /> التقرير
          </Button>
        )}
        {p.status === "archived" && onPurge && (
          <DeleteX testId={`ws-purge-${p.id}`} className="self-center !w-8 !h-8 shrink-0" title="حذف المشروع نهائياً؟"
            desc="سيُحذف المشروع المؤرشف وكل محتواه وحملاته وتقاريره نهائياً ولا يمكن استعادته." onConfirm={() => onPurge(p)} />
        )}
      </div>
    </div>
  );
}

function CampaignCard({ c, onOpen, onReport }) {
  const pct = c.posts ? Math.round(((c.produced + c.uploaded + (c.measured || 0)) / (3 * c.posts)) * 100) : 0;
  return (
    <div className="rounded-2xl border border-border bg-card p-5 hover:border-primary/40 transition-colors" data-testid={`ws-campaign-${c.id}`}>
      <h3 className="font-bold truncate">{c.name}</h3>
      <p className="text-xs text-muted-foreground mb-3">{c.project_name} · {c.platform}</p>
      <div className="grid grid-cols-3 gap-2 text-center mb-3">
        {[["منشورات", c.posts], ["مولّدة", c.produced], ["مرفوعة", c.uploaded]].map(([l, v]) => (
          <div key={l} className="rounded-xl bg-secondary py-2"><div className="font-bold">{v}</div><div className="text-[10px] text-muted-foreground">{l}</div></div>
        ))}
      </div>
      <div className="h-1.5 rounded-full bg-secondary overflow-hidden mb-4"><div className="h-full bg-sky-400" style={{ width: `${pct}%` }} /></div>
      <div className="flex gap-2">
        <Button size="sm" onClick={() => onOpen(c)} className="flex-1 bg-primary hover:bg-primary/90 text-white gap-1" data-testid={`ws-open-campaign-${c.id}`}>
          فتح الحملة <ArrowLeft className="w-3.5 h-3.5" />
        </Button>
        <Button size="sm" variant="outline" onClick={() => onReport(c)} className="border-border" data-testid={`ws-campaign-report-${c.id}`}><FileBarChart className="w-3.5 h-3.5" /></Button>
      </div>
    </div>
  );
}

export default function Workspace() {
  const { user } = useAuth();
  const { selectProject } = useProjects();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const section = params.get("s") || "full";
  const rtype = params.get("r") || "progress";

  useEffect(() => { api.get("/workspace").then((r) => setData(r.data)).catch(() => setData({ projects: [], campaigns: [] })); }, []);

  const groups = useMemo(() => {
    const ps = data?.projects || [];
    const live = ps.filter((p) => p.status !== "archived");
    return {
      full: live.filter((p) => p.mode === "full"),
      ready: live.filter((p) => p.mode === "ready"),
      standalone: live.filter((p) => p.mode === "standalone"),
      campaigns: data?.campaigns || [],
      reports: ps.filter((p) => p.has_report),
      archived: ps.filter((p) => p.status === "archived"),
      r_progress: ps.filter((p) => p.mode !== "standalone"),
      r_strategy: ps.filter((p) => p.flags?.strategy),
      r_brand: ps.filter((p) => p.flags?.strategy),
    };
  }, [data]);

  const items = (groups[section] || []).filter((x) => !q || `${x.name} ${x.project_name || ""} ${x.business_type || ""}`.includes(q));
  const reportItems = groups[`r_${rtype}`] || [];
  const pick = (id) => { setQ(""); setParams({ s: id, r: rtype }); };
  const pickReport = (id) => setParams({ s: section, r: id });
  const openTypedReport = (p) => {
    selectProject(p.id);
    if (rtype === "progress") return navigate(`/app/report/${p.id}`);
    navigate(rtype === "brand" ? "/app/strategy#identity" : "/app/strategy");
  };
  const purge = async (p) => {
    try { await api.delete(`/projects/${p.id}/permanent`); toast.success("تم حذف المشروع نهائياً"); setData((d) => ({ ...d, projects: d.projects.filter((x) => x.id !== p.id) })); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  const openProject = (p) => { selectProject(p.id); navigate(p.status === "archived" ? "/app/reports" : p.next_path); };
  const openReport = (p) => navigate(`/app/report/${p.project_id || p.id}`);
  const openCampaign = (c) => { selectProject(c.project_id); navigate("/app/campaigns"); };

  if (!data) return <div className="py-24 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  const cur = SECTIONS.find((s) => s.id === section) || SECTIONS[0];

  return (
    <div className="space-y-6 min-w-0 overflow-x-hidden" data-testid="workspace-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs text-muted-foreground">أهلاً {user?.name?.split(" ")[0] || ""} 👋</p>
          <h1 className="text-2xl sm:text-3xl font-black flex items-center gap-2"><LayoutGrid className="w-6 h-6 text-primary" /> لوحة العمل</h1>
        </div>
        <Button size="sm" onClick={() => navigate("/app/projects")} className="bg-accent hover:bg-accent/90 text-white gap-2" data-testid="ws-new-project">
          <Plus className="w-4 h-4" /> مشروع جديد
        </Button>
      </div>

      <Counters sections={PROJECT_SECTIONS} groups={groups} active={section} onPick={pick} />
      <Reminders />
      <div className="grid gap-4 lg:grid-cols-[auto_1fr] items-start">
        <StartHere />
      </div>

      <section data-testid="ws-projects-section">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h2 className="text-base font-bold">المشاريع النشطة والمؤرشفة <span className="text-muted-foreground font-normal text-sm">— {cur.label} ({items.length})</span></h2>
          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ابحث بالاسم…" data-testid="ws-search"
              className="w-full h-9 rounded-xl bg-secondary border border-border pe-9 ps-3 text-sm outline-none focus:border-primary" />
          </div>
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-4" data-testid="ws-sections">
          {PROJECT_SECTIONS.map((s, i) => <SectionTile key={s.id} s={s} i={i} count={groups[s.id].length} active={s.id === section} onClick={() => pick(s.id)} />)}
        </div>
        <AnimatePresence mode="wait">
          <motion.div key={section} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}
            className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="ws-items">
            {items.length === 0 ? (
              <div className="col-span-full rounded-2xl border border-dashed border-border p-10 text-center" data-testid="ws-empty">
                <cur.icon className="w-9 h-9 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground mb-4 text-sm">لا توجد عناصر في «{cur.label}» بعد.</p>
                <Button size="sm" onClick={() => navigate("/app/projects")} variant="outline" className="border-border gap-2"><Plus className="w-4 h-4" /> ابدأ الآن</Button>
              </div>
            ) : section === "campaigns" ? (
              items.map((c) => <CampaignCard key={c.id} c={c} onOpen={openCampaign} onReport={openReport} />)
            ) : (
              items.map((p) => <ProjectCard key={p.id} p={p} onOpen={openProject} onReport={openReport} onPurge={purge} />)
            )}
          </motion.div>
        </AnimatePresence>
      </section>

      <section data-testid="ws-reports-section">
        <h2 className="text-base font-bold mb-3">التقارير</h2>
        <div className="grid grid-cols-3 gap-2 mb-4" data-testid="ws-report-types">
          {REPORT_TYPES.map((r) => {
            const on = r.id === rtype;
            return (
              <button key={r.id} type="button" onClick={() => pickReport(r.id)} data-testid={`ws-report-type-${r.id}`}
                className={`text-start rounded-2xl border p-3 transition-colors ${on ? "border-emerald-500 bg-emerald-500/10" : "border-border bg-card hover:border-emerald-500/50"}`}>
                <r.icon className="w-5 h-5 text-emerald-500 mb-2" />
                <div className="font-bold text-xs sm:text-sm leading-tight">{r.label}</div>
                <div className="text-[10px] text-muted-foreground mt-1 line-clamp-1 hidden sm:block">{r.hint}</div>
                <span className="inline-block mt-2 min-w-6 h-6 px-2 rounded-full bg-secondary text-[11px] font-bold leading-6 text-center" data-testid={`ws-report-count-${r.id}`}>{(groups[`r_${r.id}`] || []).length}</span>
              </button>
            );
          })}
        </div>
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="ws-report-items">
          {reportItems.length === 0 ? (
            <div className="col-span-full rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="ws-reports-empty">لا توجد تقارير من هذا النوع بعد.</div>
          ) : rtype === "brand" ? reportItems.map((p) => (
            <div key={p.id} className="glass rounded-2xl border border-border p-4 text-start min-w-0 overflow-hidden" data-testid={`ws-report-item-${p.id}`}>
              <div className="font-bold text-sm truncate">هوية-{p.name}</div>
              <div className="text-[11px] text-muted-foreground mt-1 truncate">{p.business_type} · {fmtDate(p.created_at)}</div>
              <BrandDocButton project={p} testId={`ws-brand-doc-${p.id}`} className="mt-3 w-full bg-emerald-600 hover:bg-emerald-600/90 text-white" />
            </div>
          )) : reportItems.map((p) => (
            <button key={p.id} type="button" onClick={() => openTypedReport(p)} data-testid={`ws-report-item-${p.id}`}
              className="glass rounded-2xl border border-border p-4 text-start hover:border-emerald-500/60 transition-colors">
              <div className="flex items-center justify-between gap-2">
                <div className="font-bold text-sm truncate">{p.name}</div>
                <span className="text-[11px] text-emerald-500 font-bold shrink-0">{p.progress}%</span>
              </div>
              <div className="text-[11px] text-muted-foreground mt-1 truncate">{p.business_type} · {fmtDate(p.created_at)}</div>
              <div className="mt-3 h-1.5 rounded-full bg-secondary overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${p.progress || 0}%` }} /></div>
            </button>
          ))}
        </div>
      </section>
      <ReviewWidget />
    </div>
  );
}
